import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import gsap from 'gsap';
import { createDirectionalEase } from 'util/core-transition-component/utils/timeline.utils';
import Icon from '../icon/Icon';
import Text from '../text/Text';
import type { TextRef } from '../text/Text.data';
import type { GrowingArrowButtonProps, GrowingArrowButtonRef } from './GrowingArrowButton.data';
import styles from './GrowingArrowButton.module.scss';

const duration = 0.3;
const opacity = 1;

export default function GrowingArrowButton({
  ref,
  label,
  fullBleed = false,
  onTimelineCreate,
  ...props
}: GrowingArrowButtonProps & { ref?: Ref<GrowingArrowButtonRef> }): ReactElement {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonTextRef = useRef<TextRef | null>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const buttonElement = buttonRef.current;

    if (!buttonElement) return;

    const timeline = gsap
      .timeline({ paused: true })
      .set(buttonElement, {
        opacity,
      })
      .to(
        buttonElement,
        {
          width: fullBleed ? 'clamp(200px, 100%, 300px)' : '100%',
          duration: fullBleed ? 0.5 : duration,
          ease: createDirectionalEase(timelineRef, 'power2.inOut', 'power2.in'),
        },
        '<',
      )
      .fromTo(buttonTextRef.current, { opacity: 0 }, { opacity: 1, ease: 'power2.inOut' }, '<');

    onTimelineCreate?.(timeline);
    timelineRef.current = timeline;

    return () => {
      timeline.kill();
    };
  }, [fullBleed, onTimelineCreate]);

  const play = useCallback(() => {
    timelineRef.current?.play();
  }, []);

  const reverse = useCallback(() => {
    timelineRef.current?.reverse();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      elementRef: buttonRef as RefObject<HTMLButtonElement | null>,
      play,
      reverse,
    }),
    [play, reverse],
  );

  return (
    <>
      <button
        {...props}
        className={classNames(styles.button, props.className)}
        type={props.type ?? 'button'}
      >
        <Icon name="arrow-right-light" className={classNames(styles.icon)} />
      </button>

      <button
        {...props}
        ref={buttonRef}
        className={classNames(styles.button, styles.animatedButton, props.className)}
        type={props.type ?? 'button'}
      >
        <Icon name="arrow-right-light" className={classNames(styles.icon)} />
        <Text
          ref={buttonTextRef}
          disableTriggerInOnScroll={false}
          as="span"
          size="button-label-small"
          className={classNames(styles.buttonText)}
          transitionInType="none"
          transitionOutType="none"
        >
          {label}
        </Text>
      </button>
    </>
  );
}
